import {FocusEvent, MouseEvent} from "react";
import {css} from "@panda/css";
import {visuallyHidden} from "@panda/patterns";

import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {usePricesHubTableRowHover} from "../../hooks/use_prices_hub_table_row_hover";
import {trackPricesHubPlaceClick} from "../../tracking/track_prices_hub_place_click";
import {formatPriceByUnit, formatSignedPercent} from "../../utils/format_price_m2";
import {formatRcnPeriodLabel} from "../../utils/format_rcn_period_label";
import {getCityLink} from "../../utils/get_city_link";
import {ITransactionPriceRow} from "../../utils/get_transaction_price_rows";
import {PricesHubCityIcon} from "./PricesHubCityIcon";
import {PricesHubTransactionPricesTableHoverRow} from "./PricesHubTransactionPricesTableHoverRow";
import {PricesHubTrendChip} from "./PricesHubTrendChip";

interface IProps {
    rows: ITransactionPriceRow[];
    dominantPeriodLabel: string;
    offerMonthLabel: string | null;
}

export const PricesHubTransactionPricesTable = (props: IProps) => {
    const {rows, dominantPeriodLabel, offerMonthLabel} = props;
    const dominantPeriod = formatRcnPeriodLabel(dominantPeriodLabel);
    const {hoveredRow, isVisible, metrics, wrapperRef, showRow, hide} = usePricesHubTableRowHover(rows);

    const handleWrapperBlur = (event: FocusEvent<HTMLDivElement>) => {
        const nextTarget = event.relatedTarget;

        if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
            return;
        }

        hide();
    };

    const handleOverlayMouseMove = (event: MouseEvent<HTMLAnchorElement>) => {
        const tbody = wrapperRef.current?.querySelector("tbody");

        if (!tbody) {
            return;
        }

        const {clientY} = event;

        for (const rowEl of Array.from(tbody.rows)) {
            const rect = rowEl.getBoundingClientRect();

            if (clientY < rect.top || clientY >= rect.bottom) {
                continue;
            }

            const slug = rowEl.dataset.slug;

            if (slug) {
                showRow(slug, rowEl);
            }

            return;
        }
    };

    return (
        <div ref={wrapperRef} className={tableBleedStyle} onMouseLeave={hide} onBlur={handleWrapperBlur}>
            <div className={tableFrameStyle}>
                <table className={tableStyle}>
                    <thead>
                        <tr>
                            <th scope="col" className={headerCityCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Miasto
                                </Text>
                            </th>
                            <th scope="col" className={headerNumberCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Aktualna cena ofertowa{offerMonthLabel ? ` (${offerMonthLabel})` : null}
                                </Text>
                            </th>
                            <th scope="col" className={headerDividedCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Cena ofertowa ({dominantPeriod})
                                </Text>
                            </th>
                            <th scope="col" className={headerTransactionCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Cena transakcyjna ({dominantPeriod})
                                </Text>
                            </th>
                            <th scope="col" className={headerChipCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Różnica
                                </Text>
                            </th>
                            <th scope="col" className={headerActionCellStyle}>
                                <span className={visuallyHidden()}>Szczegóły</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.slug}
                                data-slug={row.slug}
                                className={rowStyle}
                                onMouseEnter={(event) => showRow(row.slug, event.currentTarget)}
                                onFocus={(event) => showRow(row.slug, event.currentTarget)}
                            >
                                <td className={cityCellStyle}>
                                    <span className={cityStyle}>
                                        <PricesHubCityIcon slug={row.slug} size="2.8" />
                                        <a href={getCityLink(row.slug)} className={cityLinkStyle} onClick={() => trackPricesHubPlaceClick(row)}>
                                            <Text as="span" variant="body_copy_2">
                                                {row.name}
                                            </Text>
                                        </a>
                                    </span>
                                </td>
                                <td className={numberCellStyle}>
                                    <Text as="span" variant="body_copy_2">
                                        {formatPriceByUnit(row.offerPriceM2, row.unit)}
                                    </Text>
                                </td>
                                <td className={dividedCellStyle}>
                                    {row.periodOfferPriceM2 === null ? (
                                        <Text as="span" variant="body_copy_2" className={missingValueStyle}>
                                            <span className={visuallyHidden()}>brak danych</span>
                                            <span aria-hidden="true">—</span>
                                        </Text>
                                    ) : (
                                        <>
                                            <Text as="span" variant="body_copy_2">
                                                {formatPriceByUnit(row.periodOfferPriceM2, row.unit)}
                                            </Text>
                                            {row.periodLabel === dominantPeriodLabel ? null : (
                                                <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                                                    {formatRcnPeriodLabel(row.periodLabel)}
                                                </Text>
                                            )}
                                        </>
                                    )}
                                </td>
                                <td className={numberCellStyle}>
                                    <Text as="span" variant="body_copy_2">
                                        {formatPriceByUnit(row.transactionPriceM2, row.unit)}
                                    </Text>
                                    {row.periodLabel === dominantPeriodLabel ? null : (
                                        <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                                            {formatRcnPeriodLabel(row.periodLabel)}
                                        </Text>
                                    )}
                                </td>
                                <td className={numberCellStyle}>
                                    <PricesHubTrendChip isBelow={row.differencePct <= 0}>{formatSignedPercent(row.differencePct)}</PricesHubTrendChip>
                                </td>
                                <td className={actionCellStyle}>
                                    <a
                                        href={getCityLink(row.slug)}
                                        className={actionLinkStyle}
                                        aria-label={`Zobacz ceny mieszkań – ${row.name}`}
                                        onClick={() => trackPricesHubPlaceClick(row)}
                                    >
                                        <ChevronRightIcon size="2" fill="currentColor" />
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {hoveredRow && metrics ? (
                <PricesHubTransactionPricesTableHoverRow
                    row={hoveredRow}
                    dominantPeriodLabel={dominantPeriodLabel}
                    metrics={metrics}
                    isVisible={isVisible}
                    onMouseMove={handleOverlayMouseMove}
                />
            ) : null}
        </div>
    );
};

const tableBleedStyle = css({
    position: "relative",
    zIndex: 1,
    width: "calc(100% + 5.2rem)",
    maxWidth: "calc(100% + 5.2rem)",
    mx: "-2.6rem",
    px: "2.6rem",
    overflow: "visible"
});

const tableFrameStyle = css({
    width: "100%",
    overflow: "visible",
    border: "1px solid var(--colors-gray-400)",
    borderRadius: 2
});

const tableStyle = css({
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0
});

const headerCellStyle = {
    py: 1.5,
    color: "var(--colors-gray-800)",
    verticalAlign: "bottom"
} as const;

const headerCityCellStyle = css({
    ...headerCellStyle,
    width: "15.5%",
    pl: "6.2rem",
    pr: 1.5,
    textAlign: "left",
    borderTopLeftRadius: 2
});

const headerNumberCellStyle = css({
    ...headerCellStyle,
    width: "17.3%",
    px: 1.5,
    textAlign: "right"
});

const headerTransactionCellStyle = css({
    ...headerCellStyle,
    width: "22.3%",
    px: 1.5,
    textAlign: "right"
});

const headerChipCellStyle = css({
    ...headerCellStyle,
    width: "20.4%",
    px: 1.5,
    textAlign: "right"
});

/** Splits the portal's own current price from the block that belongs to the transaction period. */
const headerDividedCellStyle = css({
    ...headerCellStyle,
    width: "20.4%",
    px: 1.5,
    textAlign: "right",
    borderLeft: "1px solid var(--colors-gray-300)"
});

const headerActionCellStyle = css({
    ...headerCellStyle,
    width: "4.1%",
    borderTopRightRadius: 2
});

const rowStyle = css({
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-white)",
    cursor: "pointer",

    "&:nth-of-type(even)": {
        backgroundColor: "var(--colors-gray-100)"
    },

    "&:last-child td:first-child": {
        borderBottomLeftRadius: 2
    },

    "&:last-child td:last-child": {
        borderBottomRightRadius: 2
    }
});

const cellStyle = {
    py: 1.5,
    verticalAlign: "middle",
    whiteSpace: "nowrap"
} as const;

const cityCellStyle = css({
    ...cellStyle,
    pl: 3,
    pr: 1.5,
    textAlign: "left"
});

const cityStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: "1rem"
});

const cityLinkStyle = css({
    color: "inherit",
    textDecoration: "none",

    _hover: {
        color: "inherit",
        textDecoration: "underline"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});

const numberCellStyle = css({
    ...cellStyle,
    px: 1.5,
    textAlign: "right"
});

const dividedCellStyle = css({
    ...cellStyle,
    px: 1.5,
    textAlign: "right",
    borderLeft: "1px solid var(--colors-gray-300)"
});

const missingValueStyle = css({
    color: "var(--colors-gray-600)"
});

const periodNoteStyle = css({
    display: "block",
    opacity: 0.7
});

const actionCellStyle = css({
    ...cellStyle,
    width: "4.1%",
    pr: 2.5,
    textAlign: "right"
});

const actionLinkStyle = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    color: "rgba(23, 23, 28, 0.38)",
    borderRadius: "50%",

    _hover: {
        color: "rgba(23, 23, 28, 0.38)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});
import {FocusEvent, MouseEvent} from "react";
import {css} from "@panda/css";
import {visuallyHidden} from "@panda/patterns";

import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {usePricesHubTableRowHover} from "../../hooks/use_prices_hub_table_row_hover";
import {trackPricesHubPlaceClick} from "../../tracking/track_prices_hub_place_click";
import {formatPriceByUnit, formatSignedPercent} from "../../utils/format_price_m2";
import {formatRcnPeriodLabel} from "../../utils/format_rcn_period_label";
import {getCityLink} from "../../utils/get_city_link";
import {ITransactionPriceRow} from "../../utils/get_transaction_price_rows";
import {PricesHubCityIcon} from "./PricesHubCityIcon";
import {PricesHubTransactionPricesTableHoverRow} from "./PricesHubTransactionPricesTableHoverRow";
import {PricesHubTrendChip} from "./PricesHubTrendChip";

interface IProps {
    rows: ITransactionPriceRow[];
    dominantPeriodLabel: string;
    offerMonthLabel: string | null;
}

export const PricesHubTransactionPricesTable = (props: IProps) => {
    const {rows, dominantPeriodLabel, offerMonthLabel} = props;
    const dominantPeriod = formatRcnPeriodLabel(dominantPeriodLabel);
    const {hoveredRow, isVisible, metrics, wrapperRef, showRow, hide} = usePricesHubTableRowHover(rows);

    const handleWrapperBlur = (event: FocusEvent<HTMLDivElement>) => {
        const nextTarget = event.relatedTarget;

        if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
            return;
        }

        hide();
    };

    const handleOverlayMouseMove = (event: MouseEvent<HTMLAnchorElement>) => {
        const tbody = wrapperRef.current?.querySelector("tbody");

        if (!tbody) {
            return;
        }

        const {clientY} = event;

        for (const rowEl of Array.from(tbody.rows)) {
            const rect = rowEl.getBoundingClientRect();

            if (clientY < rect.top || clientY >= rect.bottom) {
                continue;
            }

            const slug = rowEl.dataset.slug;

            if (slug) {
                showRow(slug, rowEl);
            }

            return;
        }
    };

    return (
        <div ref={wrapperRef} className={tableBleedStyle} onMouseLeave={hide} onBlur={handleWrapperBlur}>
            <div className={tableFrameStyle}>
                <table className={tableStyle}>
                    <thead>
                        <tr>
                            <th scope="col" className={headerCityCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Miasto
                                </Text>
                            </th>
                            <th scope="col" className={headerNumberCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Aktualna cena ofertowa{offerMonthLabel ? ` (${offerMonthLabel})` : null}
                                </Text>
                            </th>
                            <th scope="col" className={headerDividedCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Cena ofertowa ({dominantPeriod})
                                </Text>
                            </th>
                            <th scope="col" className={headerTransactionCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Cena transakcyjna ({dominantPeriod})
                                </Text>
                            </th>
                            <th scope="col" className={headerChipCellStyle}>
                                <Text as="span" variant="info_txt_1">
                                    Różnica
                                </Text>
                            </th>
                            <th scope="col" className={headerActionCellStyle}>
                                <span className={visuallyHidden()}>Szczegóły</span>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.slug}
                                data-slug={row.slug}
                                className={rowStyle}
                                onMouseEnter={(event) => showRow(row.slug, event.currentTarget)}
                                onFocus={(event) => showRow(row.slug, event.currentTarget)}
                            >
                                <td className={cityCellStyle}>
                                    <span className={cityStyle}>
                                        <PricesHubCityIcon slug={row.slug} size="2.8" />
                                        <a href={getCityLink(row.slug)} className={cityLinkStyle} onClick={() => trackPricesHubPlaceClick(row)}>
                                            <Text as="span" variant="body_copy_2">
                                                {row.name}
                                            </Text>
                                        </a>
                                    </span>
                                </td>
                                <td className={numberCellStyle}>
                                    <Text as="span" variant="body_copy_2">
                                        {formatPriceByUnit(row.offerPriceM2, row.unit)}
                                    </Text>
                                </td>
                                <td className={dividedCellStyle}>
                                    {row.periodOfferPriceM2 === null ? (
                                        <Text as="span" variant="body_copy_2" className={missingValueStyle}>
                                            <span className={visuallyHidden()}>brak danych</span>
                                            <span aria-hidden="true">—</span>
                                        </Text>
                                    ) : (
                                        <>
                                            <Text as="span" variant="body_copy_2">
                                                {formatPriceByUnit(row.periodOfferPriceM2, row.unit)}
                                            </Text>
                                            {row.periodLabel === dominantPeriodLabel ? null : (
                                                <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                                                    {formatRcnPeriodLabel(row.periodLabel)}
                                                </Text>
                                            )}
                                        </>
                                    )}
                                </td>
                                <td className={numberCellStyle}>
                                    <Text as="span" variant="body_copy_2">
                                        {formatPriceByUnit(row.transactionPriceM2, row.unit)}
                                    </Text>
                                    {row.periodLabel === dominantPeriodLabel ? null : (
                                        <Text as="span" variant="info_txt_2" align="right" className={periodNoteStyle}>
                                            {formatRcnPeriodLabel(row.periodLabel)}
                                        </Text>
                                    )}
                                </td>
                                <td className={numberCellStyle}>
                                    <PricesHubTrendChip isBelow={row.differencePct <= 0}>{formatSignedPercent(row.differencePct)}</PricesHubTrendChip>
                                </td>
                                <td className={actionCellStyle}>
                                    <a
                                        href={getCityLink(row.slug)}
                                        className={actionLinkStyle}
                                        aria-label={`Zobacz ceny mieszkań – ${row.name}`}
                                        onClick={() => trackPricesHubPlaceClick(row)}
                                    >
                                        <ChevronRightIcon size="2" fill="currentColor" />
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {hoveredRow && metrics ? (
                <PricesHubTransactionPricesTableHoverRow
                    row={hoveredRow}
                    dominantPeriodLabel={dominantPeriodLabel}
                    metrics={metrics}
                    isVisible={isVisible}
                    onMouseMove={handleOverlayMouseMove}
                />
            ) : null}
        </div>
    );
};

const tableBleedStyle = css({
    position: "relative",
    zIndex: 1,
    width: "calc(100% + 5.2rem)",
    maxWidth: "calc(100% + 5.2rem)",
    mx: "-2.6rem",
    px: "2.6rem",
    overflow: "visible"
});

const tableFrameStyle = css({
    width: "100%",
    overflow: "visible",
    border: "1px solid var(--colors-gray-400)",
    borderRadius: 2
});

const tableStyle = css({
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0
});

const headerCellStyle = {
    py: 1.5,
    color: "var(--colors-gray-800)",
    verticalAlign: "bottom"
} as const;

const headerCityCellStyle = css({
    ...headerCellStyle,
    width: "15.5%",
    pl: "6.2rem",
    pr: 1.5,
    textAlign: "left",
    borderTopLeftRadius: 2
});

const headerNumberCellStyle = css({
    ...headerCellStyle,
    width: "17.3%",
    px: 1.5,
    textAlign: "right"
});

const headerTransactionCellStyle = css({
    ...headerCellStyle,
    width: "22.3%",
    px: 1.5,
    textAlign: "right"
});

const headerChipCellStyle = css({
    ...headerCellStyle,
    width: "20.4%",
    px: 1.5,
    textAlign: "right"
});

/** Splits the portal's own current price from the block that belongs to the transaction period. */
const headerDividedCellStyle = css({
    ...headerCellStyle,
    width: "20.4%",
    px: 1.5,
    textAlign: "right",
    borderLeft: "1px solid var(--colors-gray-300)"
});

const headerActionCellStyle = css({
    ...headerCellStyle,
    width: "4.1%",
    borderTopRightRadius: 2
});

const rowStyle = css({
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-white)",
    cursor: "pointer",

    "&:nth-of-type(even)": {
        backgroundColor: "var(--colors-gray-100)"
    },

    "&:last-child td:first-child": {
        borderBottomLeftRadius: 2
    },

    "&:last-child td:last-child": {
        borderBottomRightRadius: 2
    }
});

const cellStyle = {
    py: 1.5,
    verticalAlign: "middle",
    whiteSpace: "nowrap"
} as const;

const cityCellStyle = css({
    ...cellStyle,
    pl: 3,
    pr: 1.5,
    textAlign: "left"
});

const cityStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: "1rem"
});

const cityLinkStyle = css({
    color: "inherit",
    textDecoration: "none",

    _hover: {
        color: "inherit",
        textDecoration: "underline"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});

const numberCellStyle = css({
    ...cellStyle,
    px: 1.5,
    textAlign: "right"
});

const dividedCellStyle = css({
    ...cellStyle,
    px: 1.5,
    textAlign: "right",
    borderLeft: "1px solid var(--colors-gray-300)"
});

const missingValueStyle = css({
    color: "var(--colors-gray-600)"
});

const periodNoteStyle = css({
    display: "block",
    opacity: 0.7
});

const actionCellStyle = css({
    ...cellStyle,
    width: "4.1%",
    pr: 2.5,
    textAlign: "right"
});

const actionLinkStyle = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "3.2rem",
    height: "3.2rem",
    color: "rgba(23, 23, 28, 0.38)",
    borderRadius: "50%",

    _hover: {
        color: "rgba(23, 23, 28, 0.38)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});
